import { ADAPTIVE_CAPACITY_MODULES, INSUFFICIENT_EVIDENCE, QUESTION_DISABLED_VALUE, QUESTION_UNKNOWN_VALUE, RCP_VALUES } from "./Consts";
import questionModules from "./Questions";

/** Returns if a number is within a marginal amount (defaulting to 0.001) of another number */
const floatApproxEquals = (x, y, epsilon = 0.001) => {
  const diff = Math.abs(x - y);
  return diff <= epsilon;
};

const calculateScoreIndexViaThresholds = (score, thresholds) => {
  if (score === INSUFFICIENT_EVIDENCE) {
    return scoringMatrix.length - 1;
  }

  for (let i = 0; i < thresholds.length && i < 4; i++) {
    const threshold = thresholds[i];
    if (score <= threshold) {
      // -2 to avoid IE column.
      return scoringMatrix.length - 2 - i;
    }
  }

  // If we somehow didn't return on the last threshold, then it must be EV.
  return 0;
};


// Rows represent high to low (and then insufficient) scores for observedResponseScore.
// Columns represent high to low (and then insufficient) scores for traitScore.
// Anything in the last column gets overidden as "insufficientEvidence", since we require
// a valid trait score to determine the overall score:
const scoringMatrix = [
  ["EV", "EV", "HV", "HV", "EV"],
  ["EV", "HV", "HV", "MV", "HV"],
  ["HV", "HV", "MV", "LV", "MV"],
  ["HV", "MV", "MV", "LV", "LV"],
  ["EV", "HV", "MV", "LV", "insufficientEvidence"]
];

const calculateOverallCcviScore = (traitScore, observedModeledResponseScore) => {
  // Comment out to allow calculation with observedModeledResponseScore alone:
  if (traitScore === INSUFFICIENT_EVIDENCE) return "insufficientEvidence";
  const traitIndex = calculateScoreIndexViaThresholds(traitScore, [5.23, 6.33, 6.95, 28.45]);
  const observedModeledResponseIndex = calculateScoreIndexViaThresholds(observedModeledResponseScore, [0.1667, 0.5, 0.8335, 1]);
  return scoringMatrix[observedModeledResponseIndex][traitIndex];
};

const calculateModuleKCategory = (observedModeledResponseScore) => {
  if (observedModeledResponseScore === INSUFFICIENT_EVIDENCE) return 'IE';
  const observedModeledResponseIndex = calculateScoreIndexViaThresholds(observedModeledResponseScore, [0.1667, 0.5, 0.8335, 1]);
  return scoringMatrix[observedModeledResponseIndex][4]; // the last column only takes into account observed modeled response.
}

/** Returns the maximum value an answer can have based on its module's scale */
const getHighestScaleDegree = (moduleKey) => {
  return questionModules[moduleKey].scale.options.length - 1
}

const calculateAdaptiveCapacity = (surveyAnswers) => {
  let adaptiveCapacity = 0;
  let numEnteredModules = 0;
  ADAPTIVE_CAPACITY_MODULES.forEach((moduleKey) => {
    let moduleHasResponse = false;
    let answeredQuestions = 0;
    let moduleSubscore = 0;
    const sectionQuestions = questionModules[moduleKey].questions;
    sectionQuestions.forEach((question) => {
      if (surveyAnswers[question.label] >= 0) {
        moduleHasResponse = true;
        answeredQuestions++;
        moduleSubscore += surveyAnswers[question.label];
      }
    });
    if (moduleHasResponse) {
      adaptiveCapacity += moduleSubscore / (answeredQuestions * getHighestScaleDegree(moduleKey));
      numEnteredModules++;
    }
  });
  if (numEnteredModules < ADAPTIVE_CAPACITY_MODULES.length - 1) {
    adaptiveCapacity = INSUFFICIENT_EVIDENCE;
  }
  return adaptiveCapacity === 0 ? 0.01 : adaptiveCapacity;
}

const calculateTraitScore = (
  climateExposure,
  climateExposureModifier,
  adaptiveCapacity,
  adaptiveCapacityModifier
) => {
   if ([climateExposure,
    climateExposureModifier,
    adaptiveCapacity,
    adaptiveCapacityModifier
  ].indexOf(INSUFFICIENT_EVIDENCE) !== -1) {
    return INSUFFICIENT_EVIDENCE;
  }
  const modifiedAdaptiveCapacity = adaptiveCapacity * adaptiveCapacityModifier;
  const overallExposure = 1 + climateExposure + climateExposureModifier;
  return overallExposure * modifiedAdaptiveCapacity;
}

const calculateObservedModeledResponse = (surveyAnswers) => {
  const sectionKQuestions = questionModules.K.questions.map((question) => question.label);
  let sum = 0;
  let answeredQuestions = 0;
  sectionKQuestions.forEach((question) => {
    const answer = surveyAnswers[question];
    if (answer >= 0) {
      sum += answer;
      answeredQuestions++;
    }
  });

  if (answeredQuestions === 0) {
    return INSUFFICIENT_EVIDENCE;
  }
  return sum / (answeredQuestions * getHighestScaleDegree('K'));
}

const calculateClimateExposureModifier = (surveyAnswers) => {
  const sectionBQuestions = questionModules.B.questions.map((question) => question.label);
  let sum = 0;
  let notAllQuestionsAnswered = false;
  sectionBQuestions.forEach((question) => {
    const answer = surveyAnswers[question];
    if (answer >= 0) {
      sum += answer;
    } else {
      notAllQuestionsAnswered = true;
    }
  });
  if (notAllQuestionsAnswered) {
    return INSUFFICIENT_EVIDENCE;
  }
  const total = sectionBQuestions.length * getHighestScaleDegree('B');
  return sum / total;
}

const calculateAdaptiveCapacityModifier = (surveyAnswers) => {
  const sectionJQuestions = questionModules.J.questions.map((question) => question.label);
  let sum = 0;
  let answeredQuestions = 0;
  sectionJQuestions.forEach((question) => {
    const answer = surveyAnswers[question];
    if (answer >= 0) {
      answeredQuestions ++;
      sum += answer;
    }
  });

  if (answeredQuestions < 3) {
    return INSUFFICIENT_EVIDENCE;
  }

  const total = sectionJQuestions.length * getHighestScaleDegree('J');
  return 1 + 0.5 * (sum / total);
}

const getRelativeClimateExposure = (climateExposure) => {
  if (climateExposure === INSUFFICIENT_EVIDENCE) return 'insufficientEvidence';
  if (climateExposure < 0.4) return 'climateLower';
  if (climateExposure < 0.53) return 'climateAverage';
  return 'climateHigher';
};

const getRelativeAdaptiveCapacity = (adaptiveCapacity) => {
  if (adaptiveCapacity === INSUFFICIENT_EVIDENCE) return 'insufficientEvidence';
  if (adaptiveCapacity <= 2.5) return 'acHigh';
  if (adaptiveCapacity <= 3) return 'acModeratelyHigh';
  if (adaptiveCapacity <= 3.6) return 'acModeratelyLow';
  return 'acLow';
}

const calculateModuleCompleteness = (questions, surveyAnswers) => {
  let totalQuestions = questions.length;
  let answeredQuestions = 0.0;
  questions.forEach(
    (question) => {
      const questionLabel = question.label;
      if (surveyAnswers[questionLabel] === QUESTION_DISABLED_VALUE) {
        totalQuestions -= 1;
      } else if (surveyAnswers[questionLabel] !== QUESTION_UNKNOWN_VALUE) {
        answeredQuestions += 1.0;
      }
    },
  );
  let percentAnswered = (answeredQuestions / totalQuestions) * 100;
  if (floatApproxEquals(percentAnswered, 100)) {
    percentAnswered = 100;
  } else if (floatApproxEquals(percentAnswered, 0)) {
    percentAnswered = 0;
  } else {
    percentAnswered = percentAnswered.toFixed(0);
  }
  return percentAnswered;
}


/**
 * Calculates most useful values that can be derived from the assessment state
 * @param {object} surveyAnswers an object with question labels for keys and numbers as values
 * @param {number[]} exposureResults an array with the mean_rcp85 value and the mean_rcp45 value
 * @returns {AssessmentResults} An object containing detailed derived data
 */
const calculateAllResults = (surveyAnswers, exposureResults) => {
  const adaptiveCapacity = calculateAdaptiveCapacity(surveyAnswers);
  const observedModeledResponseScore = calculateObservedModeledResponse(surveyAnswers);
  const climateExposureModifier = calculateClimateExposureModifier(surveyAnswers);
  const adaptiveCapacityModifier = calculateAdaptiveCapacityModifier(surveyAnswers);

  const exposureResultInfos = exposureResults.map((climateExposure, idx) => {
    const traitScore = calculateTraitScore(climateExposure, climateExposureModifier, adaptiveCapacity, adaptiveCapacityModifier);
    return {
      climateExposure,
      traitScore,
      climateExposureCategory: getRelativeClimateExposure(climateExposure),
      overallCategory: calculateOverallCcviScore(traitScore, observedModeledResponseScore),
      moduleKCategory: calculateModuleKCategory(observedModeledResponseScore),
      rcpKey: RCP_VALUES[idx]
    }
  });

  const moduleCompleteness = {};
  moduleCompleteness.A = exposureResults[0] === INSUFFICIENT_EVIDENCE ? 0 : 100;
  Object.entries(questionModules).forEach(([moduleKey, { questions }]) => {
    moduleCompleteness[moduleKey] = calculateModuleCompleteness(questions, surveyAnswers);
  });
  moduleCompleteness.adaptiveCapacity = adaptiveCapacity !== INSUFFICIENT_EVIDENCE;
  moduleCompleteness.climateExposureModifier = climateExposureModifier !== INSUFFICIENT_EVIDENCE;
  moduleCompleteness.adaptiveCapacityModifier = adaptiveCapacityModifier !== INSUFFICIENT_EVIDENCE;
  moduleCompleteness.overallCategory = exposureResultInfos[0]?.overallCategory !== 'insufficientEvidence';

  return {
    adaptiveCapacityInfo: {
      score: adaptiveCapacity,
      categoryKey: getRelativeAdaptiveCapacity(adaptiveCapacity)
    },
    exposureResultInfos,
    moduleCompleteness
  };
}

/**
 * @typedef {Object} AssessmentResults
 * @property {AdaptiveCapacityInfo} [adaptiveCapacityInfo]
 * @property {ExposureResultInfo[]} [exposureResultInfos]
 * @property {Object.<string, (number|string|boolean)>} moduleCompleteness
 */

/**
 * @typedef {Object} AdaptiveCapacityInfo
 * @property {number} score
 * @property {string} categoryKey
 */

/**
 * @typedef {Object} ExposureResultInfo
 * @property {number} climateExposure
 * @property {number} traitScore
 * @property {string} climateExposureCategory
 * @property {string} overallCategory
 * @property {string} moduleKCategory
 * @property {string} rcpKey
 */

export { calculateAllResults };
